import type { ErrorStatusValues, SystemErrorStatusValues } from '@repo/common';
import type { ErrorInfo, ReactNode } from 'react';

import { ErrorStatus } from '@repo/common';
import { Component } from 'react';

import { Layout } from '../Layout/Layout';
import { DotWebComponentErrorDisplay } from '../dot-web-component-display';
import { VerificationFailed } from '../verification-result/verification-failed';

import { DotWebComponentsError } from '@/errors/dot-web-components-error';
import { RivaasError } from '@/errors/rivaas-error';
import { SystemError } from '@/errors/system-error';

const ERROR_STATUS_VALUES = new Set<string>(Object.values(ErrorStatus));

// SystemError.status spans every SystemErrorStatus code, but this boundary can only render a
// verification fail reason (ErrorStatusValues). Codes outside that vocabulary — e.g.
// PALM_FEATURE_DISABLED, a session-creation gate rather than a fail reason — fall back to
// UNABLE_TO_VERIFY instead of leaking a non-renderable status.
function toErrorStatusValue(status?: SystemErrorStatusValues): ErrorStatusValues {
  return status && ERROR_STATUS_VALUES.has(status) ? (status as ErrorStatusValues) : ErrorStatus.UNABLE_TO_VERIFY;
}

type State = {
  error: unknown;
  hasError: boolean;
};

type Props = {
  children?: ReactNode;
};

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: undefined,
  };

  public static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    this.logError(error, errorInfo);
  }

  private logError(error: unknown, errorInfo: ErrorInfo): void {
    if (error instanceof RivaasError) {
      error.log(errorInfo);
    } else {
      console.error(error, errorInfo);
    }
  }

  private renderError() {
    const { error } = this.state;

    if (error instanceof DotWebComponentsError) {
      return <DotWebComponentErrorDisplay message={error.message} />;
    }

    let errorStatus: ErrorStatusValues = ErrorStatus.UNABLE_TO_VERIFY;
    let errorMessage = undefined;

    if (error instanceof SystemError) {
      errorStatus = toErrorStatusValue(error.status);
      errorMessage = error.tracingId;
    }

    return <VerificationFailed errorMessage={errorMessage} failReason={errorStatus} />;
  }

  render() {
    const { hasError } = this.state;

    if (!hasError) {
      return this.props.children;
    }

    return <Layout>{this.renderError()}</Layout>;
  }
}
